<script lang="ts" setup>
import { getQueryParameter } from '@atabix/core'
import { computed, onMounted, ref, useSlots } from 'vue'
import { useI18n } from 'vue-i18n'

import { AAlert, ALoginForm, type ForgotPasswordFormPayload, type LoginFormPayload, offsetHexColor } from '../../../main'
import AForgotPasswordForm from './AForgotPasswordForm.vue'
import ALoginHeader from './ALoginHeader.vue'
import AResetPasswordForm from './AResetPasswordForm.vue'

export type ALoginLayoutColors = {
  primary: string
  background: string
  accent?: string
}

export type ALoginState = 'login' | 'forgot' | 'reset' | 'register' | 'message' | 'custom'

export type ALoginLayoutFormPayload = {
  username: string
  password: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FormInputs = any[]

export interface ALoginLayoutProperties {
  logo?: string
  background?: string
  title?: string
  error?: string | undefined
  loading?: boolean
  isLoadingLogin?: boolean
  isLoadingForgot?: boolean
  isLoadingRegister?: boolean
  message?: {
    type: 'success' | 'error'
    title: string
    description: string
  }
  labelUsername?: string | undefined
  description?: string | undefined
  payload?: ALoginLayoutFormPayload
  colors?: ALoginLayoutColors
  form?: boolean | FormInputs
  register?: boolean
  forgot?: boolean
  customTitle?: string
  customDescription?: string
  /**
   * Portal also requires a type (entity type)
   */
  isTypeRequired?: boolean
}

export interface ALoginLayoutEmits {
  (event: 'clickOutsideOrKeyupEscape'): void
  (event: 'submit', data: ALoginLayoutFormPayload): void
  (event: 'forgot', data: ForgotPasswordFormPayload): void
  (event: 'reset', data: ForgotPasswordFormPayload): void
}

defineOptions({ inheritAttrs: false })

const state = defineModel<ALoginState>({ default: 'login' })
const resetToken = ref<string>('')
const resetUsername = ref<string>('')
const resetType = ref<string>('')
const slots = useSlots()

const { t } = useI18n({
  messages: {
    en: {
      error: 'Error',
      username: 'Username',
      email: 'Email',
      loginWith: 'Or login with',
      'login.title': 'Login to {title}',
      'login.description': 'Please enter your username and password.',
      'forgot.title': 'Forgot Password',
      'forgot.description': 'Please enter your username to request a new password.',
      'reset.title': 'Password setup',
      'reset.description': 'Please enter your password to change it.',
      'register.title': 'Register',
      'register.description': 'Please enter your details to register.',
    },
    nl: {
      error: 'Foutmelding',
      title: 'Inloggen bij {title}',
      username: 'Gebruikersnaam',
      email: 'E-mailadres',
      description: 'Vul hieronder uw gebruikersnaam en wachtwoord.',
      loginWith: 'Of login met',
      'login.title': 'Inloggen bij {title}',
      'login.description': 'Vul hieronder uw gebruikersnaam en wachtwoord.',
      'forgot.title': 'Wachtwoord vergeten',
      'forgot.description': 'Voer je gebruikersnaam in voor een nieuw wachtwoord.',
      'reset.title': 'Wachtwoord instellen',
      'reset.description': 'Voer je wachtwoord in om het te wijzigen.',
      'register.title': 'Registereren',
      'register.description': 'Vul hieronder uw gegevens in om te registreren.',
    },
    de: {
      error: 'Fehler',
      username: 'Benutzername',
      email: 'E-Mail-Adresse',
      loginWith: 'Oder anmelden mit',
      'login.title': 'Anmelden bei {title}',
      'login.description': 'Bitte geben Sie Ihren Benutzernamen und Ihr Passwort ein.',
      'forgot.title': 'Passwort vergessen',
      'forgot.description': 'Bitte geben Sie Ihren Benutzernamen ein, um ein neues Passwort anzufordern',
      'reset.title': 'Passwort einrichten',
      'reset.description': 'Bitte geben Sie Ihr Passwort ein, um es zu ändern.',
      'register.title': 'Registrieren',
      'register.description': 'Bitte geben Sie Ihre Daten ein, um sich zu registrieren.',
    },
  },
})

const properties = withDefaults(defineProps<ALoginLayoutProperties>(), {
  logo: '/logo-white.svg',
  background: undefined,
  title: 'MijnPortaal',
  description: undefined,
  loading: false,
  labelUsername: undefined,
  error: undefined,
  form: true,
  forgot: true,
  register: false,
  message: () => ({
    type: 'success',
    title: '',
    description: '',
  }),
  payload: () => ({
    username: '',
    password: '',
  }),
  colors: () => ({
    primary: '#0A3D3A',
    background: '#FFFFFF',
    accent: '#14D2AB',
  }),
  customTitle: '',
  customDescription: '',
})

const emit = defineEmits<ALoginLayoutEmits>()

const computedTitle = computed(() => (state.value === 'custom' ? properties.customTitle : t(`${state.value}.title`, { title: properties.title })))
const computedDescription = computed(() => (state.value === 'custom' ? properties.customDescription : t(`${state.value}.description`)))

async function onSubmitLogin(payload: LoginFormPayload): Promise<void> {
  emit('submit', payload)
}

async function onSubmitForgot(payload: ForgotPasswordFormPayload): Promise<void> {
  emit('forgot', payload)
}

async function onSubmitReset(payload: ForgotPasswordFormPayload): Promise<void> {
  emit('reset', payload)
}

function onClickForgot(): void {
  state.value = 'forgot'
}

function onClickRegister(): void {
  state.value = 'register'
}

onMounted(initialize)

async function initialize() {
  const token = getQueryParameter('token', true)
  const username = getQueryParameter('username', true)
  const type = getQueryParameter('type', true)

  if (token && username && (!properties.isTypeRequired || type)) {
    state.value = 'reset'
    resetToken.value = token
    resetUsername.value = username
    resetType.value = type
  }
}
</script>

<template>
  <div class="atabix-auth-layout" :style="{ backgroundColor: colors.primary }">
    <div class="atabix-auth-login" :style="{ backgroundColor: colors.background }">
      <AAlert
        v-if="error"
        :class="['inset-x-11 top-11 my-8 w-11/12 md:absolute md:my-0 md:w-auto']"
        :subtitle="error"
        :title="t('error')"
        variant="error"
      />
      <div class="atabix-auth-login__forms">
        <slot name="header" />
        <ALoginHeader :description="state === 'login' && !form ? '' : computedDescription" :title="computedTitle" />

        <template v-if="state === 'login'">
          <ALoginForm
            v-if="form"
            :forgot="forgot"
            :is-loading-forgot
            :is-loading-login
            :is-loading-register
            :label-username="labelUsername || t('username')"
            :loading="!!loading"
            :register="register"
            @forgot="onClickForgot"
            @register="onClickRegister"
            @submit="onSubmitLogin"
          />

          <div v-if="slots.other && form" class="atabix-auth-divider">
            <span :style="{ backgroundColor: colors.background }">{{ t('loginWith') }}</span>
          </div>

          <slot v-if="state === 'login'" name="other" />
        </template>

        <slot v-if="state === 'forgot'" name="forgot">
          <AForgotPasswordForm
            :label-username="labelUsername || t('username')"
            :loading="!!loading"
            @cancel="state = 'login'"
            @submit="onSubmitForgot"
          />
        </slot>

        <slot v-if="state === 'register'" name="register" />

        <slot v-if="state === 'custom'" name="custom" />

        <slot v-if="state === 'reset'" name="reset">
          <AResetPasswordForm
            :label-username="labelUsername || t('username')"
            :loading="!!loading"
            :token="resetToken"
            :type="resetType"
            :username="resetUsername"
            @cancel="state = 'login'"
            @submit="onSubmitReset"
          />
        </slot>

        <slot name="footer" />
      </div>
    </div>
    <div class="atabix-auth-splash__container" :style="{ backgroundImage: background ? `url('${background}')` : undefined }">
      <div class="atabix-auth-splash">
        <div v-if="title" class="atabix-auth-title">
          <h1 :style="{ color: colors.accent || offsetHexColor(colors.primary) }">{{ title }}</h1>
        </div>
        <img v-if="logo" :alt="title" :src="logo" />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.atabix-auth-layout {
  @apply min-h-screen grid relative;

  grid-template-columns: 1fr 0;

  grid-template-rows: 1fr;
  grid-column-gap: 0px;
  grid-row-gap: 0px;

  @media screen and (min-width: 1200px) {
    grid-template-columns: 1fr 62%;
  }
}

.atabix-auth-splash__container {
  @apply flex items-center justify-center;
  @apply w-full h-full;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
}

.atabix-auth-splash {
  grid-area: 1 / 2 / 2 / 3;

  @apply flex items-center justify-center;
  @apply w-full h-full;
  @apply backdrop-blur-sm;

  img {
    @apply h-16;
    @apply select-none;
  }
}

.atabix-auth-login {
  @apply flex flex-col items-center justify-center relative;

  .atabix-auth-login__forms {
    @apply grid;
    @apply max-w-lg;
    @apply gap-8;
    @apply p-4;
    @apply w-full;

    .atabix-auth-login__forms-title {
      @apply text-lg;
      @apply font-bold;
    }

    .atabix-auth-login__forms-description {
      @apply text-foreground-subtle;
    }
  }

  .atabix-auth-login__error {
    @apply flex;
    @apply flex-row;
    @apply items-center;
    @apply text-foreground-alert;

    .atabix-auth-login__error-icon {
      @apply w-6;
    }

    .atabix-auth-login__error-text {
      @apply ml-1;
      @apply text-base;
    }
  }
}

.atabix-auth-title {
  @apply hidden;
  @apply absolute top-0 right-0;
  @apply p-10 text-2xl font-semibold;
  font-family: 'Gilroy-SemiBold', sans-serif;

  @media screen and (min-width: 1200px) {
    @apply block;
  }
}

.atabix-auth-divider {
  @apply relative;
  @apply flex;
  @apply items-center;
  @apply justify-center;

  &::before {
    content: '';
    @apply w-full;
    @apply h-[1px];
    @apply absolute;
    @apply left-0;
    @apply top-1/2;
    @apply z-0;
    @apply bg-form-border;
  }

  span {
    @apply block;
    @apply px-2;
    @apply text-foreground-subtle;
    @apply z-10;
  }
}
</style>
